import type { BaseModalFCProps } from '@/components/modals/modalsManager';
import { Dialog } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

import type { PluginDialogProps, PluginDialogSize } from './define-plugin';
import type { ComponentType } from 'react';

type PluginDialogShellProps = BaseModalFCProps & {
  title: string;
  component: ComponentType<PluginDialogProps>;
  props: object;
  size: PluginDialogSize;
};

const SIZES: Record<PluginDialogSize, string> = {
  default: 'max-h-[85dvh] w-[calc(100vw-2rem)] max-w-3xl rounded-2xl',
  // A fixed height, so a dialog whose content shrinks, such as search results, never resizes or moves
  wide: 'h-[82dvh] w-[calc(100vw-3rem)] max-w-[1080px] rounded-3xl sm:w-[calc(100vw-6rem)]',
};

/** Core's frame for a plugin's dialog. The plugin's component fills it below the title and scrolls its own content */
export const PluginDialogShell = ({
  open,
  onOpenChange,
  title,
  component: Content,
  props,
  size,
}: PluginDialogShellProps) => (
  <Dialog.Root open={open} onOpenChange={onOpenChange}>
    <Dialog.Content
      aria-describedby={undefined}
      data-size={size}
      // Masked in session recordings, as plugin pages and Home sections are
      className={cn(
        'no-record flex flex-col gap-0 overflow-hidden p-0',
        SIZES[size]
      )}
    >
      <Dialog.Title className="shrink-0 px-6 pt-6 text-sm font-normal text-foreground-muted md:px-8">
        {title}
      </Dialog.Title>
      <Content {...props} close={() => onOpenChange(false)} />
    </Dialog.Content>
  </Dialog.Root>
);
